import React, { useEffect, useState } from 'react';
import FilterDropdown from '../../../components/Filter/components/FilterDropdown';
import { translate } from '../../../services/translate';
import { UseFormReturn } from 'react-hook-form';
import FormDropDownPicker from '../../../components/FormDropDownPicker';
import { useIsFocused } from '@react-navigation/native';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../context/Auth';
import { dispatchService } from '../../../services/central-api/dispatch';

type Props = {
    zIndex: number;
    disabled?: boolean;
    required?: boolean;
    emptyOption?: boolean;
    actingBodyId: number | null;
    onItemChange?: (val?: UnitCategory) => void;
} & ({
    form?: undefined;
    fieldName?: undefined;
    value: number | null;
    setValue: (value: number | null) => void;
    multiple: false;
} | {
    form?: undefined;
    fieldName?: undefined;
    value: number[];
    setValue: React.Dispatch<React.SetStateAction<number[]>>;
    multiple: true;
} | {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form: UseFormReturn<any, any, any>;
    fieldName: string;
    value?: undefined;
    setValue?: undefined;
    multiple: false;
});

function CategoriesDropdown({ value, setValue, zIndex, disabled, form, fieldName, required, emptyOption, multiple, actingBodyId, onItemChange }: Props) {
    const { user: authenticatedUser } = useAuth();
    const [items, setItems] = useState<{ label: string; value: number; }[]>([]);

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['getUnitCategoriesSimplified' + actingBodyId?.toString()],
        queryFn: () => dispatchService.getSimplifiedUnitCategories(actingBodyId != null ? actingBodyId : undefined),
        enabled: !!authenticatedUser && isFocused && actingBodyId != 0,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;
    const { data } = queryResult;

    useEffect(() => {
        if (!authenticatedUser || !data) {
            return;
        }

        const options = data.map(unitCategory => ({
            label: unitCategory.name, value: unitCategory.id!
        }));

        if (emptyOption) {
            options.unshift({ label: '', value: 0 });
        }
        setItems(options);
    }, [data, authenticatedUser]);

    if (form && fieldName) {
        const watchedValue = form.watch(fieldName);

        useEffect(() => {
            if (data) {
                let item = undefined;
                if (watchedValue) {
                    item = data.find(i => i.id === watchedValue);
                }
                onItemChange?.(item);
            }
        }, [watchedValue, data]);

        return <FormDropDownPicker
            label={translate('category')}
            items={items}
            multiple={false}
            zIndex={zIndex}
            disabled={disabled}
            required={required}
            fieldName={fieldName}
            form={form}
            isLoading={isLoading}
        />;
    } else if (multiple) {
        return (
            <FilterDropdown
                label={translate('category')}
                items={items}
                multiple={true}
                mode="BADGE"
                value={value}
                setValue={setValue}
                zIndex={zIndex}
                disabled={disabled}
                required={required}
                isLoading={isLoading}
            />
        );
    } else if (value !== undefined && setValue !== undefined) {
        useEffect(() => {
            if (data) {
                let item = undefined;
                if (value) {
                    item = data.find(i => i.id === value);
                }
                onItemChange?.(item);
            }
        }, [value, data]);
        return (
            <FilterDropdown
                label={translate('category')}
                items={items}
                multiple={false}
                setValue={(selectedValue) => {
                    const selectedCategory = selectedValue(value);
                    setValue(selectedCategory == value ? '' : selectedCategory);
                }}
                value={value}
                zIndex={zIndex}
                disabled={disabled}
                required={required}
            />
        );
    }
}

export default CategoriesDropdown;
